import React, { useCallback, useEffect, useState } from "react";
// form
import { useIntakeFormStore } from "../intakeFormState";
// controller (from layout)
import { useNavigate, useOutletContext } from "react-router-dom";
import type { IntakeOutletContext } from "../IntakeFormLayout";
// utils
import { useTranslation } from "react-i18next";
// UI
import { ConsentToTreatDoc } from "../ConsentToTreatDoc";
import { IntakeFormScreenContainer } from "../IntakeFormScreenContainer";
import { Typography } from "../../../components/Typography/Typography";
import { MightierButton } from "../../../components/MightierButton/MightierButton";
import { Scrollable } from "../../../components/Scrollable/Scrollable";

export interface ConsentDocumentInfo {
  disclosureFileName: string;
  disclosureMd5: string;
}

export const IntakeFormConsentScreen = React.memo(() => {
  const { handleConsentSubmit } = useIntakeFormStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [hasScrolledAllTheWay, setHasScrolledAllTheWay] = useState(false);
  const [consentDocumentInfo, setConsentDocumentInfo] = useState<ConsentDocumentInfo | null>(null);

  const outletContext: IntakeOutletContext = useOutletContext();
  const { onSubmit: afterSubmit } = outletContext;

  const onSubmit = useCallback(() => {
    if (!consentDocumentInfo) return;
    handleConsentSubmit({
      signatureRecordedAt: new Date().toISOString(),
      methodOfSignature: "electronic_checkbox",
      ...consentDocumentInfo,
    });
    afterSubmit();
  }, [handleConsentSubmit, afterSubmit, consentDocumentInfo]);

  const handleCancelClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // NOTE: if the user hits this page, their consent is "reset" and
  // they cannot proceed!!
  useEffect(() => {
    handleConsentSubmit(undefined);
  }, [handleConsentSubmit]);

  const handleReachBottomOfScroll = useCallback(() => {
    setHasScrolledAllTheWay(true);
  }, []);

  return (
    <IntakeFormScreenContainer title={t("intakeConsent.header")}>
      <div className="m-auto md:w-10/12">
        <Typography variant="muted" className="text-center">
          {t("intakeConsent.disclaimer")}
        </Typography>
      </div>

      {/* disclaimer scrollview */}
      <div className="mt-2">
        <Scrollable onReachBottom={handleReachBottomOfScroll}>
          <ConsentToTreatDoc setConsentDocumentInfo={setConsentDocumentInfo} />
        </Scrollable>
      </div>

      {/* buttons -> "I agree" sets value to true in storage and proceeds to next */}
      <div className="mt-8 flex flex-row justify-between">
        <MightierButton variant="primary" onClick={handleCancelClick}>
          {t("intakeConsent.declineButton")}
        </MightierButton>
        <MightierButton variant={hasScrolledAllTheWay ? "primary" : "disabled"} onClick={onSubmit}>
          {t("intakeConsent.acceptButton")}
        </MightierButton>
      </div>
    </IntakeFormScreenContainer>
  );
});

IntakeFormConsentScreen.displayName = "IntakeFormConsentScreen";
